/// <reference path="../../css.d.ts" />
"use client";

// Source : UIArc `registry/components/tooltip` (MIT © 2026 Elia Kuratli), commit 7238f98196b147a8d50ce467abcbad310d4d8414.
// Adaptations UBM (lot U2), toutes ADDITIVES (défauts = source) : côtés gauche/droite, délai, classe de contenu, contenu
// non survolable, et engagement d'hôte (une fenêtre qui passe derrière n'affiche aucune bulle). Rendu, fenêtre de
// chaînage partagée, texte morphé et mouvements : ceux de la source.
// Correction U2 (revue FIN1 point 3) : un déclencheur qui porte DÉJÀ un `aria-describedby` le garde, et la bulle s'y
// ajoute pendant l'ouverture (description préalable SEULE après fermeture). La règle Slot de Radix faisait gagner la
// prop de l'enfant et perdre la bulle. L'id de la jumelle `role="tooltip"` est lu dans le contenu Radix monté ; refs et
// handlers de l'enfant passent intacts (même élément cloné, même moteur Radix).
// Port de portée (lot E, 06/10/2026), ADDITIF : `contentProps` pose les `data-*` et variables `--*` d'une façade (apparence
// locale du MacScope) sur le nœud en portail de la bulle ; ni classe ni peinture, et l'absence rend la source à l'identique.
import { cloneElement, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import type { ReactElement, ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";
import { useOverlayHostEngaged } from "../../overlays/OverlayHost";
import "../../styles/foundation.css";
import styles from "./tooltip.module.css";

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  side?: "top" | "bottom" | "left" | "right";
  /** Délai d'apparition à froid (ms). Défaut 250 (source). */
  delay?: number;
  /** Classe ajoutée à la bulle (habillage d'une façade). */
  className?: string;
  /** La bulle se ferme dès que le pointeur quitte le déclencheur (pas de survol de la bulle). Défaut `false` (source). */
  disableHoverableContent?: boolean;
  /**
   * Portée de la façade sur la bulle en PORTAIL (lot E) : attributs `data-*` (apparence locale) et variables CSS `--*`, posés
   * sur le même nœud. Rien d'autre ne passe — ni classe, ni propriété de peinture : la bulle reste celle de la source. Absent = source.
   */
  contentProps?: TooltipScopeProps;
}

export interface TooltipScopeProps {
  style?: { [variable: `--${string}`]: string };
  [attribute: `data-${string}`]: string | undefined;
}

/** Seuls les `data-*` et les variables `--*` de la portée atteignent la bulle ; le reste est ignoré. */
function scopeOf(props: TooltipScopeProps | undefined): Record<string, unknown> {
  if (!props) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) if (key.startsWith("data-") && typeof value === "string") out[key] = value;
  const variables = Object.entries(props.style ?? {}).filter(([key, value]) => key.startsWith("--") && typeof value === "string");
  if (variables.length) out.style = Object.fromEntries(variables);
  return out;
}

const DELAY = 250;
const SKIP_WINDOW = 300;

/* Every Tooltip brings its own provider, so the skip window is shared here: while any tooltip is open, and briefly after the last one closes, the next opens without delay or travel. */
let warm = false;
let openCount = 0;
let coolTimer = 0;
const listeners = new Set<() => void>();
const warmth = {
  subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
  get: () => warm,
  set(next: boolean) { if (warm === next) return; warm = next; listeners.forEach(listener => listener()); },
  opened() { openCount += 1; window.clearTimeout(coolTimer); warmth.set(true); },
  closed() { openCount = Math.max(0, openCount - 1); if (openCount) return; window.clearTimeout(coolTimer); coolTimer = window.setTimeout(() => warmth.set(false), SKIP_WINDOW); },
};

/** String content crossfades when it changes while open, and the bubble springs to the new text size. */
function TooltipText({ text }: { text: string }) {
  const reduced = useReducedMotion();
  const measure = useRef<HTMLSpanElement>(null);
  const measured = useRef<string | null>(null);
  const [size, setSize] = useState<{ width: number; height: number; animate: boolean } | null>(null);
  useLayoutEffect(() => {
    const node = measure.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.borderBoxSize?.[0];
      const current = node.textContent;
      const animate = measured.current !== null && measured.current !== current;
      measured.current = current;
      setSize({ width: Math.ceil(box?.inlineSize ?? node.offsetWidth), height: Math.ceil(box?.blockSize ?? node.offsetHeight), animate });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <motion.span className={styles.text} initial={false} animate={size ? { width: size.width, height: size.height } : undefined} transition={size?.animate && !reduced ? motionTokens.spring.morph : { duration: 0 }}>
    <span ref={measure} className={styles.measure} aria-hidden="true">{text}</span>
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span key={text} className={styles.line} initial={reduced ? false : { opacity: 0, y: "0.3em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: "-0.3em", filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.instant, ease: [...motionTokens.ease.standard] } }} transition={{ duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}>{text}</motion.span>
    </AnimatePresence>
  </motion.span>;
}

export function Tooltip({ content, children, side = "top", delay = DELAY, className, disableHoverableContent, contentProps }: TooltipProps) {
  const isWarm = useSyncExternalStore(warmth.subscribe, warmth.get, () => false);
  const engaged = useOverlayHostEngaged();
  const reducedMotion = useReducedMotion();
  // Controlled so the instant flag lands in the same render that mounts the content (Radix reports uncontrolled changes a frame late).
  const [open, setOpen] = useState(false);
  const [instant, setInstant] = useState(false);
  const shown = open && engaged;
  useEffect(() => {
    if (!shown) return;
    warmth.opened();
    return warmth.closed;
  }, [shown]);
  const prior = (children.props as { "aria-describedby"?: string })["aria-describedby"];
  const contentRef = useRef<HTMLDivElement>(null);
  const [tipId, setTipId] = useState<string | null>(null);
  // Sans dépendances : le portail Radix ne monte le contenu qu'au rendu suivant l'ouverture ; on relit jusqu'à stabilité.
  useLayoutEffect(() => {
    const id = shown ? contentRef.current?.querySelector<HTMLElement>('[role="tooltip"]')?.id || null : null;
    setTipId((current) => (current === id ? current : id));
  });
  const trigger = prior === undefined
    ? children
    : cloneElement(children, { "aria-describedby": shown && tipId ? `${prior} ${tipId}` : prior } as Record<string, unknown>);
  return <TooltipPrimitive.Provider delayDuration={delay} skipDelayDuration={0} disableHoverableContent={disableHoverableContent}>
    <TooltipPrimitive.Root open={shown} delayDuration={isWarm ? 0 : delay} onOpenChange={next => { if (next) setInstant(warmth.get()); setOpen(next); }}>
      <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        {/* A string bubble names its hidden `role="tooltip"` twin by `aria-label`, so the description reads the text once, not the measure copy too. */}
        <TooltipPrimitive.Content {...scopeOf(contentProps)} ref={contentRef} className={[styles.tooltip, className].filter(Boolean).join(" ")} data-motion={reducedMotion ? "reduced" : undefined} data-instant={instant || undefined} side={side} sideOffset={8} collisionPadding={12} aria-label={typeof content === "string" || typeof content === "number" ? String(content) : undefined}>
          {typeof content === "string" || typeof content === "number" ? <TooltipText text={String(content)}/> : content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>;
}

export default Tooltip;
